import { Component } from "react";
import AsyncSelect from "react-select/async";
import Select, { components } from "react-select";
import debounce from "debounce-promise";

class DebounceAsync extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedOption: props?.selectedOption || (props.isMulti ? [] : []),
      asyncOptions: [], // To store dynamically loaded options for async mode
    };

    const wait = this.props.waitTime || 1000; // milliseconds
    const loadOptions = (inputValue) => this._handleChange(inputValue);
    this.debouncedLoadOptions = debounce(loadOptions, wait);
    this.isMulti =
      typeof this.props.isMulti === "boolean" ? this.props.isMulti : false;
    this.isAsync =
      typeof this.props.isAsync === "boolean" ? this.props.isAsync : true;
    this.SelectComponent = this.isAsync ? AsyncSelect : Select;
  }

  componentDidMount() {
    const { selectedOption } = this.props;
    if (selectedOption) {
      this.setState({ selectedOption });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedOption !== this.props.selectedOption) {
      this.setState({ selectedOption: this.props.selectedOption || [] });
    }
  }

  _handleChange = (inputValue) => {
    const { loadOptions } = this.props;

    return new Promise((resolve, reject) => {
      loadOptions(
        inputValue,
        (results) => {
          if (this.isMulti && this.isAsync) {
            this.setState((prevState) => ({
              asyncOptions: [
                ...prevState.selectedOption.filter(
                  (sel) => !results.some((opt) => opt.value === sel.value)
                ),
                ...results,
              ],
            }));
            return resolve(this.state.asyncOptions);
          }
          resolve(results);
        },
        reject
      );
    });
  };

  handleSelectChange = (selectedOption) => {
    this.setState({ selectedOption: selectedOption || [] });

    // Call the passed onChange handler
    if (this.props.onChange) {
      this.props.onChange(selectedOption, this);
    }
  };

  // Sort selected options to appear at the top
  sortedOptions = (options) => [
    ...this.state.selectedOption,
    ...options.filter(
      (opt) => !this.state.selectedOption.some((sel) => sel.value === opt.value)
    ),
  ];

  formatGroupLabel = () =>
    this.isMulti && this.state.selectedOption.length > 0
      ? `${this.state.selectedOption.length} item${
          this.state.selectedOption.length > 1 ? "s" : ""
        } selected`
      : this.props.placeholder || "Search...";

  CheckboxOption = (props) => {
    // Render checkbox only when isMulti is true
    if (!this.isMulti) {
      return <components.Option {...props}>{props.label}</components.Option>;
    }
    return (
      <components.Option {...props}>
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null} // React-Select handles changes internally
          style={{ marginRight: 10 }}
        />
        {props.label}
      </components.Option>
    );
  };

  ValueComponent = ({ children, ...props }) => {
    const { getValue, hasValue } = props;
    const length = getValue().length;
    if (!hasValue) {
      return (
        <components.ValueContainer {...props}>
          {children}
        </components.ValueContainer>
      );
    }
    return (
      <components.ValueContainer {...props}>
        {children}
      </components.ValueContainer>
    );
  };

  render() {
    const {
      results,
      loadOptions,
      onChange,
      placeholder,
      options,
      components,
      disabled,
      ...rest
    } = this.props;

    return (
      <this.SelectComponent
        loadOptions={this.isAsync ? this.debouncedLoadOptions : undefined} // Used for async loading
        placeholder={this.formatGroupLabel()}
        isSearchable={!disabled}
        isClearable={!disabled}
        escapeClearsValue={!disabled}
        closeMenuOnSelect={!this.isMulti}
        isDisabled={disabled}
        components={{
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
          Option: this.CheckboxOption,
          ValueContainer: this.ValueComponent,
          ...(this.props?.components ? this.props.components : {}),
        }}
        loadingMessage={(inputObject) =>
          `Searching for ${inputObject.inputValue}`
        }
        noOptionsMessage={() => (results?.length === 0 ? null : "No result")}
        classNamePrefix="react-select"
        value={this.state.selectedOption}
        onChange={this.handleSelectChange}
        isMulti={this.isMulti}
        options={
          this.isAsync
            ? this.sortedOptions(this.state.asyncOptions)
            : this.isMulti
            ? this.sortedOptions(this.props?.options || [])
            : this.props?.options || []
        }
        hideSelectedOptions={false}
        controlShouldRenderValue={!this.isMulti}
        onBlurResetsInput={false}
        cacheOptions // Improves performance for AsyncSelect
        defaultOptions={
          this.isAsync
            ? this.sortedOptions(this.state.asyncOptions)
            : this.state.selectedOption
        }
        {...rest}
      />
    );
  }
}

export default DebounceAsync;
